import { useEffect, useMemo, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, ArrowUpRight, Mail, Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Seo, articleSchema, breadcrumbSchema } from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
};

export const MD_COMPONENTS = {
  h2: (props) => (
    <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-[-0.025em] mt-12 mb-5 text-white" {...props} />
  ),
  h3: (props) => (
    <h3 className="font-display text-xl font-semibold tracking-[-0.02em] mt-10 mb-4 text-white" {...props} />
  ),
  p: (props) => <p className="text-[17px] leading-[1.72] text-white/80 mb-6" {...props} />,
  strong: (props) => <strong className="text-white font-semibold" {...props} />,
  em: (props) => <em className="text-white/85 italic" {...props} />,
  a: ({ href, ...props }) => {
    const isInternal = href && href.startsWith("/");
    return isInternal ? (
      <Link to={href} className="text-[#b967ff] hover:underline underline-offset-2" {...props} />
    ) : (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-[#b967ff] hover:underline underline-offset-2" {...props} />
    );
  },
  ul: (props) => <ul className="list-none ml-0 mb-6 space-y-3" {...props} />,
  ol: (props) => <ol className="list-none ml-0 mb-6 space-y-3" {...props} />,
  li: ({ ordered: _ordered, ...props }) => (
    <li className="text-[17px] leading-[1.72] text-white/80 pl-6 relative before:absolute before:left-0 before:top-[12px] before:w-2 before:h-2 before:rounded-full before:bg-[#ac62ea]/70" {...props} />
  ),
  blockquote: (props) => (
    <blockquote className="border-l-2 border-[#ac62ea] pl-5 my-7 text-white/75 italic text-[17px] leading-relaxed" {...props} />
  ),
  code: ({ inline, ...props }) =>
    inline ? (
      <code className="bg-white/8 text-[#b967ff] px-1.5 py-0.5 rounded text-[0.9em] font-mono" {...props} />
    ) : (
      <code className="block bg-white/5 border border-white/10 rounded-xl p-4 my-6 text-sm font-mono text-white/85 overflow-x-auto" {...props} />
    ),
  hr: () => <hr className="my-12 border-white/10" />,
};

export default function InsightDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    // Fetch the article + full list in parallel. The list powers the
    // "Related insights" block and is cheap enough to refetch per slug (small
    // JSON, cached by the browser after the first hit).
    Promise.all([
      axios.get(`${API}/articles/${slug}`),
      axios.get(`${API}/articles`),
    ])
      .then(([postRes, listRes]) => {
        if (cancelled) return;
        setPost(postRes.data);
        setAllPosts(Array.isArray(listRes.data) ? listRes.data : []);
      })
      .catch(() => !cancelled && setNotFound(true))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [slug]);

  const related = useMemo(() => {
    if (!post || !allPosts.length) return [];
    const others = allPosts.filter((p) => p.slug !== post.slug);
    // Prefer same-category; if fewer than 3, top up with the newest across all others.
    const sameCategory = others.filter((p) => p.category === post.category);
    const topUp = others.filter((p) => p.category !== post.category);
    return [...sameCategory, ...topUp].slice(0, 3);
  }, [post, allPosts]);

  /* The <Seo> component now handles all imperative meta-tag overrides + JSON-LD.
     No more custom useEffect needed to mutate static index.html meta tags. */

  if (loading) {
    return (
      <div className="bg-[#0a1628] text-white min-h-screen flex items-center justify-center" data-testid="insight-loading">
        <span className="text-sm text-white/45 font-mono uppercase tracking-[0.14em]">Loading insight…</span>
      </div>
    );
  }

  if (notFound || !post) return <Navigate to="/insights" replace />;

  const description = post.excerpt || post.subtitle || "";

  return (
    <article className="bg-[#0a1628] text-white min-h-screen" data-testid={`insight-${slug}`}>
      <Seo
        title={`${post.title} · SideB Insights`}
        description={description}
        canonical={`https://sideb.io/insights/${post.slug}`}
        ogType="article"
        ogImage={post.cover_image || undefined}
        keywords={post.category || undefined}
        schema={[
          articleSchema({
            headline: post.title,
            description,
            slug: post.slug,
            datePublished: post.published_at,
            author: post.author || "SideB",
            section: post.category,
            image: post.cover_image || undefined,
          }),
          breadcrumbSchema([
            { name: "Home", url: "https://sideb.io/" },
            { name: "Insights", url: "https://sideb.io/insights" },
            { name: post.title, url: `https://sideb.io/insights/${post.slug}` },
          ]),
        ]}
      />

      <header className="border-b border-white/10">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 pt-12 pb-14 lg:pt-16 lg:pb-20">
          <Link to="/insights" className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.16em] text-white/55 hover:text-[#ac62ea] transition-colors mb-10" data-testid="insight-back-link">
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to Insights
          </Link>

          <div className="flex items-center gap-3 text-xs mb-5 flex-wrap">
            {post.category && (
              <>
                <span className="text-[#ac62ea] font-mono uppercase tracking-[0.16em]">{post.category}</span>
                <span className="text-white/30">·</span>
              </>
            )}
            {post.read_minutes ? (
              <>
                <span className="text-white/50">{post.read_minutes} min read</span>
                <span className="text-white/30">·</span>
              </>
            ) : null}
            <span className="text-white/50">{formatDate(post.published_at)}</span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-[56px] font-semibold tracking-[-0.035em] leading-[1.04]">
            {post.title}
          </h1>
          {post.subtitle && post.subtitle !== post.title && (
            <p className="mt-5 text-lg text-white/65 leading-relaxed">{post.subtitle}</p>
          )}

          <div className="mt-10 flex items-center gap-3 pt-6 border-t border-white/10">
            <div className="w-9 h-9 rounded-full bg-[#ac62ea]/15 border border-[#ac62ea]/40 flex items-center justify-center text-[#ac62ea] font-display font-semibold text-sm">
              {(post.author || "SideB").split(" ").map((n) => n[0]).join("").slice(0, 2)}
            </div>
            <div>
              <div className="text-sm font-medium text-white">{post.author || "SideB Consulting Studio"}</div>
            </div>
            <a
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`${window.location.origin}/api/share/insights/${post.slug}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-1.5 text-xs text-white/70 hover:text-white hover:border-[#ac62ea] transition-colors"
              data-testid="insight-share-linkedin"
            >
              <Linkedin className="h-3.5 w-3.5" /> Share
            </a>
          </div>
        </div>
        {post.cover_image && (
          <div className="max-w-5xl mx-auto px-6 lg:px-10 pb-14 lg:pb-20">
            <img src={post.cover_image} alt="" className="w-full aspect-[3/2] object-cover rounded-2xl border border-white/10" data-testid="insight-cover" />
          </div>
        )}
      </header>

      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-14 lg:py-20" data-testid="insight-body">
        <div className="prose-insight">
          <ReactMarkdown components={MD_COMPONENTS}>{post.body || ""}</ReactMarkdown>
        </div>
      </div>

      <section className="bg-[#0f1e33] border-t border-white/10">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-14 lg:py-16">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-[-0.025em] leading-snug">
            Seeing this pattern in your stack?
          </h2>
          <p className="mt-3 text-white/65 leading-relaxed">
            Walk us through your environment. We&rsquo;ll come back with the configuration critique that matters.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Button asChild className="rounded-full bg-[#ac62ea] hover:bg-[#8a48cf] h-11 px-5 text-sm font-medium" data-testid="insight-cta-contact">
              <Link to="/contact">
                <Mail className="mr-2 h-4 w-4" />
                Contact us
              </Link>
            </Button>
            <Link to="/insights" className="text-sm font-medium text-[#ac62ea] hover:underline">
              Read more insights
            </Link>
          </div>
        </div>
      </section>
      {related.length > 0 && <RelatedInsights posts={related} currentCategory={post.category} />}
    </article>
  );
}

/* ------------------------------ RELATED INSIGHTS ------------------------------ */

const RelatedInsights = ({ posts, currentCategory }) => {
  const sameCategoryCount = posts.filter((p) => p.category === currentCategory).length;
  const label = sameCategoryCount >= posts.length
    ? "More from this category"
    : sameCategoryCount > 0
      ? "You might also like"
      : "Latest from the field";
  return (
    <section className="bg-[#0a1628] border-t border-white/10" data-testid="insight-related">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <div className="flex items-baseline justify-between mb-10">
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-white/55">
            {label}
          </div>
          <Link
            to="/insights"
            className="text-[11px] font-mono uppercase tracking-[0.14em] text-white/55 hover:text-[#ac62ea] transition-colors"
            data-testid="insight-related-all"
          >
            All insights
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-10 lg:gap-x-12">
          {posts.map((p) => (
            <Link
              key={p.slug}
              to={`/insights/${p.slug}`}
              className="group block border-t border-white/10 pt-6 hover:border-[#ac62ea]/60 transition-colors"
              data-testid={`insight-related-card-${p.slug}`}
            >
              <div className="flex items-center gap-2 text-xs mb-3 flex-wrap">
                {p.category && (
                  <span className="text-[#ac62ea] font-mono uppercase tracking-[0.14em]">{p.category}</span>
                )}
                {p.read_minutes ? (
                  <>
                    <span className="text-white/30">·</span>
                    <span className="text-white/55 font-mono uppercase tracking-[0.12em] text-[11px]">{p.read_minutes} min read</span>
                  </>
                ) : null}
              </div>
              <h3 className="font-display text-xl font-semibold tracking-[-0.02em] leading-snug text-white group-hover:text-[#b967ff] transition-colors">
                {p.title}
              </h3>
              {(p.excerpt || p.subtitle) && (
                <p className="mt-3 text-sm text-white/65 leading-relaxed line-clamp-3">{p.excerpt || p.subtitle}</p>
              )}
              <div className="mt-5 flex items-center justify-between">
                <div className="text-xs text-white/45 font-mono uppercase tracking-[0.12em]">
                  {formatDate(p.published_at)}
                </div>
                <ArrowUpRight className="h-4 w-4 text-white/40 group-hover:text-[#ac62ea] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};
